import { ReactElement, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { DocplannerDoctorMapping, DocplannerDoctorMappingQuery } from '@dreryk/egabinet-docplanner-frontend-api';
import { FirstName, LastName } from '@dreryk/person-name-common-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import {
   createDynamicColumnsWidth,
   HorizontalAlign,
   RowDetailsExpander,
   RowDetailsRenderer,
   TableColumnConfiguration,
   ValueRendererProps,
} from '@dreryk/react-components';

import { doctorsMappingsTranslationKeys } from '../../translations';

import { DocplannerAddressRowDetails } from './DocplannerAddressRowDetails';
import { EditDoctorMapping } from './EditDoctorMapping';

const getPractitionerDisplayValue = (firstName: FirstName | string, lastName: LastName | string): string =>
   `${lastName} ${firstName}`;

const StyledFacilitiesCount = styled.div`
   width: 100%;
   text-align: right;
`;
StyledFacilitiesCount.displayName = 'StyledFacilitiesCount';

// eslint-disable-next-line max-lines-per-function
export const useDoctorsMappingsTableColumnsConfiguration = (
   onAssociationSetSuccess: () => Promise<void>
): TableColumnConfiguration<DocplannerDoctorMapping>[] => {
   const { current: columnsWidth } = useRef(
      createDynamicColumnsWidth({
         docplannerPractitioners: '50%',
         eGabinetAssociation: '50%',
         facilities: '82px',
         edit: '54px',
         rowExpander: '50px',
      })
   );
   return useMemo(
      () => [
         {
            name: 'docplannerPractitioners',
            label: doctorsMappingsTranslationKeys.docplannerPractitioners,
            accessor: (bean: DocplannerDoctorMapping) =>
               getPractitionerDisplayValue(bean.externalFirstName, bean.externalLastName),

            renderer: ({ bean }: ValueRendererProps<DocplannerDoctorMapping>) => {
               return <>{getPractitionerDisplayValue(bean.externalFirstName, bean.externalLastName)}</>;
            },
            width: columnsWidth.docplannerPractitioners,
            sortingDisabled: true,
         },
         {
            name: 'eGabinetAssociation',
            label: doctorsMappingsTranslationKeys.eGabinetAssociation,
            accessor: (bean: DocplannerDoctorMapping) =>
               bean.practitioner
                  ? getPractitionerDisplayValue(bean.practitioner.firstName, bean.practitioner.lastName)
                  : '',

            renderer: ({ bean }: ValueRendererProps<DocplannerDoctorMapping>) => {
               if (!bean.practitioner) {
                  return <>{'-'}</>;
               }
               return <>{getPractitionerDisplayValue(bean.practitioner.firstName, bean.practitioner.lastName)}</>;
            },
            width: columnsWidth.eGabinetAssociation,
            sortingDisabled: true,
         },
         {
            name: 'facilities',
            label: doctorsMappingsTranslationKeys.facilities,
            accessor: () => '',

            renderer: ({ bean }: ValueRendererProps<DocplannerDoctorMapping>) => {
               return <StyledFacilitiesCount>{bean.mappings.length}</StyledFacilitiesCount>;
            },
            width: columnsWidth.facilities,
            sortingDisabled: true,
         },
         {
            name: 'edit',
            label: '',
            accessor: () => '',

            renderer: ({ bean }) => {
               return (
                  <EditDoctorMapping
                     docplannerDoctorId={bean.externalDoctorId}
                     docplannerDoctorFirstName={bean.externalFirstName}
                     docplannerDoctorLastName={bean.externalLastName}
                     doctorAddressMappings={bean.mappings}
                     associatedPractitioner={bean.practitioner}
                     onAssociationSetSuccess={onAssociationSetSuccess}
                  />
               );
            },
            width: columnsWidth.edit,
            sortingDisabled: true,
         },
         {
            name: 'expander',
            label: '',
            accessor: () => '',
            textAlign: HorizontalAlign.CENTER,
            width: columnsWidth.rowExpander,
            sortingDisabled: true,

            renderer: (renderProps: ValueRendererProps<DocplannerDoctorMapping>): ReactElement => {
               return <RowDetailsExpander {...renderProps} testId={'expander'} />;
            },
         },
      ],
      [columnsWidth, onAssociationSetSuccess]
   );
};

export const useDoctorsMappingsTableRowDetailsRenderer = (): RowDetailsRenderer<DocplannerDoctorMapping> =>
   useCallback<RowDetailsRenderer<DocplannerDoctorMapping>>(({ bean }) => {
      return bean.mappings.map(
         (mapping) =>
            (
               <DocplannerAddressRowDetails
                  externalAddressId={mapping.externalAddressId}
                  externalAddressName={mapping.externalName}
                  externalFacilityAddress={`${mapping.externalStreetName} ${mapping.externalPostCode}`}
                  facilityName={mapping.healthcareProviderLocation?.name}
                  facilityAddress={mapping.healthcareProviderLocation?.address}
               />
            ) ?? null
      );
   }, []);

export const useDoctorsMappingsTableDataConfigurationByCqrsQuery = (): LazyLoadedDataConfigurationByCqrsQuery<DocplannerDoctorMapping> => {
   return useLazyLoadedDataConfigurationByCqrsQuery(() => new DocplannerDoctorMappingQuery());
};
